// Sidebar / bottom navigation. `roles` limits who sees an entry.
import { ALL, OWNER, STAFF } from '../constants/permissions';
import type { Role } from '../constants/roles';

export interface NavItem {
  to: string;
  label: string;
  /** Bottom bar label on phones */
  shortLabel?: string;
  /** Label shown to workers instead of label */
  workerLabel?: string;
  icon: string;
  roles: Role[];
  /** Only exact matches light this entry */
  end?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', shortLabel: 'Home', icon: 'grid', roles: ALL, end: true },
      { to: '/jobs/board', label: 'Work board', workerLabel: 'My work board', icon: 'columns', roles: ALL },
    ],
  },
  {
    label: 'Business',
    items: [
      { to: '/customers', label: 'Customers', icon: 'users', roles: STAFF },
      { to: '/quotations', label: 'Quotations', icon: 'quote', roles: STAFF },
      { to: '/jobs', label: 'Service jobs', shortLabel: 'Jobs', workerLabel: 'My jobs', icon: 'wrench', roles: ALL },
      { to: '/invoices', label: 'Invoices', icon: 'receipt', roles: STAFF },
      { to: '/work-rates', label: 'Work rates', icon: 'bolt', roles: STAFF },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/materials', label: 'Materials', icon: 'box', roles: STAFF },
      { to: '/stock', label: 'Stock', icon: 'layers', roles: STAFF },
      { to: '/purchases', label: 'Purchases', icon: 'cart', roles: STAFF },
      { to: '/vendors', label: 'Suppliers', icon: 'truck', roles: STAFF },
    ],
  },
  {
    label: 'Workforce',
    items: [
      { to: '/attendance', label: 'Attendance', icon: 'calendar', roles: STAFF },
      { to: '/payroll', label: 'Salary & wages', icon: 'rupee', roles: OWNER },
      { to: '/staff-advances', label: 'Staff advances', icon: 'moneyOut', roles: OWNER },
      { to: '/team', label: 'Team', icon: 'user', roles: OWNER },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/transactions', label: 'Payments', icon: 'wallet', roles: STAFF },
      { to: '/expenses', label: 'Expenses', icon: 'moneyOut', roles: STAFF },
      { to: '/outstanding', label: 'Outstanding', icon: 'clock', roles: STAFF },
    ],
  },
  {
    label: 'Insights & tools',
    items: [
      { to: '/reports', label: 'Reports', icon: 'chart', roles: STAFF },
      { to: '/templates', label: 'Quote templates', icon: 'quote', roles: STAFF },
      { to: '/masters', label: 'Master data', icon: 'layers', roles: STAFF },
      { to: '/activity', label: 'Activity log', icon: 'activity', roles: OWNER },
      { to: '/settings', label: 'Settings', icon: 'settings', roles: OWNER },
      { to: '/help', label: 'Help & guide', icon: 'info', roles: ALL },
    ],
  },
  {
    label: 'Website',
    items: [
      { to: '/website', label: 'Website content', icon: 'layers', roles: OWNER },
    ],
  },
];

export function navFor(role?: Role): NavGroup[] {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items
      .filter((i) => !!role && i.roles.includes(role))
      .map((i) => ({ ...i, label: role === 'worker' && i.workerLabel ? i.workerLabel : i.label })),
  })).filter((g) => g.items.length);
}

export const BOTTOM_NAV = ['/', '/jobs', '/customers', '/invoices'];

// Labels for URL parts that are not in the sidebar (used by breadcrumbs).
export const SEGMENT_LABELS: Record<string, string> = { new: 'New', edit: 'Edit', convert: 'Create job', adjust: 'Adjust', account: 'Account', password: 'Change password' };
